import React from "react"
import axios from "axios"
import _ from "lodash"
import { styled } from "@mui/material/styles"
import { Container, Paper, MenuItem, Select, CircularProgress, Tab, Tabs } from "@mui/material"
import { BarChart } from "@mui/x-charts/BarChart"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import ThemeProviderWrapper from "./ThemeProviderWrapper"

const Header = styled("h2")`
  font-size: 2.8rem;
  font-weight: 900;
  margin-bottom: 50px;
`
const Snippet = styled("p")`
  font-size: 16px;
  padding-bottom: 10px;
`
const MetricTab = styled(Tab)(({ theme, selected }) => ({
  padding: "12px 8px",
  margin: "0px 8px",
  fontWeight: 600,
  color: selected ? theme.palette.primary.main : theme.palette.text.primary,
  "&:focus": {
    backgroundColor: "#FFFFFF",
  },
  "&:first-of-type": {
    marginLeft: 0,
  },
}))
const TabIcon = styled("i")`
  margin-right: 8px;
`
const OptionsContainer = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const metricOptions = [
  { metric: "downloads", icon: "fas fa-download" },
  { metric: "plays", icon: "fas fa-play" },
  { metric: "favorites", icon: "fas fa-heart" },
  { metric: "comments", icon: "fas fa-comments" },
]

const periodOptions = [
  "all time",
  "12 months",
  "6 months",
  "3 months",
  "30 days",
  "1 week",
]

export default class AnalyticsDashboard extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      metricToShow: props.defaultMetric || "downloads",
      timePeriod: props.defaultTimePeriod || "30 days",
      chartData: null,
      totalMetricValue: this.calculateTotalValue(props.defaultData),
      isLoading: false,
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.metricToShow !== this.state.metricToShow ||
      prevState.timePeriod !== this.state.timePeriod
    ) {
      this.getAnalyticsData()
    }
  }

  calculateTotalValue = (data) => {
    if (_.isEmpty(data)) return 0
    return Object.values(data).reduce((total, value) => total + value, 0)
  }

  getAnalyticsData = async () => {
    const { analyticsUrl } = this.props
    const { metricToShow, timePeriod } = this.state
    axios.defaults.headers.common["Accept"] = "application/json"

    const params = new URLSearchParams({
      metric: metricToShow,
      period: timePeriod,
    })
    this.setState({ isLoading: true })
    try {
      const response = await axios({
        url: `${analyticsUrl}?${params.toString()}`,
        responseType: "json",
      })
      const chartData = response.data
      this.setState({
        chartData,
        totalMetricValue: this.calculateTotalValue(chartData.data),
        isLoading: false,
      })
    } catch (error) {
      console.error("Error fetching data:", error)
      this.setState({ isLoading: false })
    }
  }

  setBarChartData = (data) => {
    if (_.isEmpty(data)) return []
    return Object.entries(data).map(([label, value]) => ({
      label: label.toString(),
      value,
    }))
  }

  handleMetricSelection = (metric) => {
    this.setState((prevState) => {
      const timePeriodOptions = metric === "plays" && !["3 months", "30 days", "1 week"].includes(prevState.timePeriod)
        ? "3 months"
        : prevState.timePeriod
      return { metricToShow: metric, timePeriod: timePeriodOptions }
    })
  }

  handlePeriodSelection = (event) => {
    const selectedPeriod = event.target.value
    this.setState((prevState) => {
      if (prevState.metricToShow === "plays" && !["3 months", "30 days", "1 week"].includes(selectedPeriod)) {
        return { timePeriod: "3 months" }
      }
      return { timePeriod: selectedPeriod }
    })
  }

  renderMetricsTabs() {
    return metricOptions.map(({ metric, icon }) => (
      <MetricTab
        key={metric}
        value={metric}
        onClick={() => this.handleMetricSelection(metric)}
        label={
          <span>
            <TabIcon className={icon} aria-hidden="true" />
            {metric}
          </span>
        }
        aria-selected={this.state.metricToShow === metric}
      />
    ))
  }

  renderBarChart() {
    const { chartData, isLoading } = this.state
    const { defaultData } = this.props

    const dataToRender = chartData ? chartData.data : defaultData
    const bars = this.setBarChartData(dataToRender)

    return (
      <div>
        {isLoading ? (
          <div
            style={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", height: "300px" }}
            role="status"
            aria-live="polite"
          >
            <CircularProgress role="status" aria-busy="true" aria-live="polite" aria-label="Loading data" />
            <h5>Loading...</h5>
          </div>
        ) : (
          <BarChart
            series={[
              {
                data: bars.map((bar) => bar.value),
                color: "#7471f7",
              },
            ]}
            xAxis={[
              {
                data: bars.map((bar) => bar.label),
                scaleType: "band",
              },
            ]}
            height={300}
            aria-label="Bar chart displaying analytics data"
          />
        )}
      </div>
    )
  }

  render() {
    const { metricToShow, timePeriod, totalMetricValue } = this.state
    const { currentUser } = this.props

    const filteredPeriodOptions = metricToShow === "plays"
      ? ["3 months", "30 days", "1 week"]
      : periodOptions

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ThemeProviderWrapper>
          <Container>
            <Header>Analytics Dashboard</Header>
            <Snippet>
              Hey {currentUser.display_name}! Your samples have had <strong>{totalMetricValue} {metricToShow}</strong> in the last {timePeriod}
              {totalMetricValue !== 0 && " 🙌"}
            </Snippet>
            <Paper
              sx={{
                marginTop: "20px",
                marginBottom: "40px",
                padding: "10px 20px",
              }}
            >
              <OptionsContainer>
                <Tabs
                  value={metricToShow}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  textColor="inherit"
                  indicatorColor="primary"
                  aria-label="Metric selection tabs"
                >
                  {this.renderMetricsTabs()}
                </Tabs>
                <Select
                  value={timePeriod}
                  onChange={this.handlePeriodSelection}
                  displayEmpty
                  variant="outlined"
                  aria-label="Select time period"
                >
                  {filteredPeriodOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </OptionsContainer>
            </Paper>
            <Paper sx={{ margin: "20px 0px 40px" }}>{this.renderBarChart()}</Paper>
          </Container>
        </ThemeProviderWrapper>
      </LocalizationProvider>
    )
  }
}
